import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Select,
  Button,
  Banner,
  TextContainer,
  Checkbox,
  Text,
  Badge,
  // List
} from '@shopify/polaris';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { useShopifyBilling, getPlanById } from '../services/shopifyBilling';
import { TestPromptModal } from '../components/TestPromptModal';

interface Settings {
  openaiApiKey: string;
  baseLanguage: string;
  targetLanguages: string[];
  translationPrompt: string;
  selectedResourceTypes: string[];
  maxTokens: number;
  temperature: number;
  autoTranslate: boolean;
}

interface PromptTemplate {
  id: string;
  name: string;
  description: string;
  prompt: string;
  category: 'product' | 'marketing' | 'technical' | 'general';
}

interface ShopLocale {
  locale: string;
  name: string;
  published: boolean;
  enabled: boolean;
}

const AVAILABLE_LANGUAGES = [
  { label: 'English', value: 'en' },
  { label: 'French', value: 'fr' },
  { label: 'German', value: 'de' },
  { label: 'Spanish', value: 'es' },
  { label: 'Italian', value: 'it' },
  { label: 'Portuguese', value: 'pt' },
  { label: 'Dutch', value: 'nl' },
  { label: 'Polish', value: 'pl' },
  { label: 'Swedish', value: 'sv' },
  { label: 'Finnish', value: 'fi' },
  { label: 'Danish', value: 'da' },
  { label: 'Norwegian', value: 'no' }
];

const PROMPT_TEMPLATES: PromptTemplate[] = [
  {
    id: 'product-description',
    name: 'Product Description',
    description: 'Professional product descriptions with marketing appeal',
    category: 'product',
    prompt: 'Translate this product description to {target_language}. Maintain the marketing appeal and professional tone. Keep product names, brand names, and technical specifications unchanged. Ensure the translation is natural and engaging for {target_language} speakers.'
  },
  {
    id: 'marketing-copy',
    name: 'Marketing Copy',
    description: 'Compelling marketing content with emotional appeal',
    category: 'marketing',
    prompt: 'Translate this marketing copy to {target_language}. Preserve the emotional appeal and persuasive tone. Maintain brand voice and marketing objectives. Ensure cultural appropriateness for {target_language} speakers while keeping the call-to-action clear and compelling.'
  },
  {
    id: 'technical-specs',
    name: 'Technical Specifications',
    description: 'Precise technical content with accuracy focus',
    category: 'technical',
    prompt: 'Translate this technical specification to {target_language}. Maintain technical accuracy and precision. Keep measurements, units, and technical terms consistent. Use appropriate technical terminology for {target_language} speakers.'
  },
  {
    id: 'general-content',
    name: 'General Content',
    description: 'Balanced translation for general website content',
    category: 'general',
    prompt: 'Translate this content to {target_language}. Maintain the original tone and style while ensuring natural flow in {target_language}. Preserve formatting and structure. Make the content culturally appropriate for {target_language} speakers.'
  },
  {
    id: 'seo-optimized',
    name: 'SEO Optimized',
    description: 'Search engine optimized translations',
    category: 'marketing',
    prompt: 'Translate this content to {target_language} with SEO optimization in mind. Include relevant keywords naturally in the translation. Maintain readability and user engagement while improving search visibility for {target_language} speakers.'
  },
  {
    id: 'customer-service',
    name: 'Customer Service',
    description: 'Friendly and helpful customer service content',
    category: 'general',
    prompt: 'Translate this customer service content to {target_language}. Maintain a helpful and friendly tone. Ensure clarity and politeness appropriate for {target_language} speakers. Keep instructions clear and easy to follow.'
  }
];

const RESOURCE_TYPES = [
  { label: 'Products', value: 'PRODUCT', category: 'storefront', description: 'Product titles, descriptions, and variants' },
  { label: 'Collections', value: 'COLLECTION', category: 'storefront', description: 'Collection titles and descriptions' },
  { label: 'Pages', value: 'ONLINE_STORE_PAGE', category: 'storefront', description: 'Custom pages and content' },
  { label: 'Blog Posts', value: 'ONLINE_STORE_ARTICLE', category: 'content', description: 'Blog post titles and content' },
  { label: 'Blogs', value: 'ONLINE_STORE_BLOG', category: 'content', description: 'Blog titles and descriptions' },
  { label: 'Menus', value: 'ONLINE_STORE_MENU', category: 'navigation', description: 'Navigation menu items' },
  { label: 'Metafields', value: 'METAFIELD', category: 'data', description: 'Custom product and collection metadata' },
  { label: 'Metaobjects', value: 'METAOBJECT', category: 'data', description: 'Structured content and metadata' },
  { label: 'Product Options', value: 'PRODUCT_OPTION', category: 'storefront', description: 'Product option names' },
  { label: 'Product Option Values', value: 'PRODUCT_OPTION_VALUE', category: 'storefront', description: 'Product option values' },
  { label: 'Selling Plans', value: 'SELLING_PLAN', category: 'commerce', description: 'Subscription and selling plan names' },
  { label: 'Selling Plan Groups', value: 'SELLING_PLAN_GROUP', category: 'commerce', description: 'Selling plan group names' },
  { label: 'Shop Policies', value: 'SHOP_POLICY', category: 'legal', description: 'Privacy policy, terms of service, etc.' },
  { label: 'Payment Gateways', value: 'PAYMENT_GATEWAY', category: 'commerce', description: 'Payment method names' },
  { label: 'Delivery Methods', value: 'DELIVERY_METHOD_DEFINITION', category: 'commerce', description: 'Shipping method names' },
  { label: 'Packing Slip Templates', value: 'PACKING_SLIP_TEMPLATE', category: 'fulfillment', description: 'Packing slip content' },
  { label: 'Email Templates', value: 'EMAIL_TEMPLATE', category: 'communication', description: 'Email notification content' },
  { label: 'Filters', value: 'FILTER', category: 'navigation', description: 'Product filter names' },
  { label: 'Links', value: 'LINK', category: 'navigation', description: 'Navigation link text' },
  { label: 'Theme', value: 'ONLINE_STORE_THEME', category: 'storefront', description: 'Theme customization text' }
];

const RESOURCE_CATEGORIES = [
  { value: 'all', label: 'All Resources' },
  { value: 'storefront', label: 'Storefront' },
  { value: 'content', label: 'Content' },
  { value: 'navigation', label: 'Navigation' },
  { value: 'commerce', label: 'Commerce' },
  { value: 'data', label: 'Data & Metadata' },
  { value: 'legal', label: 'Legal' },
  { value: 'fulfillment', label: 'Fulfillment' },
  { value: 'communication', label: 'Communication' }
];

const DEFAULT_TRANSLATION_PROMPT = `{
  "instructions": "You are a translation API. Translate the provided text from "{{base_language}}" to "{{translate_to}}". Your response must contain ONLY the translated text, nothing else—no explanations, no JSON, no additional text.\n\nFollow these rules strictly:\n- Preserve all HTML tags exactly as they appear (e.g., <b>text</b> becomes <b>translated text</b>).\n- Do not translate full URLs; leave them unchanged (e.g., https://example.com remains https://example.com).\n- Identify and translate handles, slugs, or tags (e.g., sequences like \"my-super-product-1\" or \"@user-handle\", which may become future URL components). Translate the words naturally, but preserve the overall format (e.g., hyphens, structure), and ensure the result uses only ASCII characters: remove accents, quotes, diacritics, or UTF-8 special characters by replacing them with their closest non-accented equivalents (e.g., \"é\" becomes \"e\", \"ñ\" becomes \"n\"). Do not add spaces or alter the slug-like structure.\n- For any other content, provide a natural, accurate translation.\n- If the text contains mixed elements, apply rules selectively to each part.\n\nExamples:\n- Input text: \"Bonjour <b>mon ami</b>, visite https://example.com/my-slug-e\"\n- If translating from \"fr\" to \"en\": Output ONLY: \"Hello <b>my friend</b>, visit https://example.com/my-slug-e\"",
  "translate_from": "{{base_language}}",
  "translate_to": "{{translate_to}}",
  "text_to_translate": "{{text}}"
}`;

export const Settings: React.FC = () => {
  const { user: _user } = useAuth();
  const billingService = useShopifyBilling();
  const [settings, setSettings] = useState<Settings>({
    openaiApiKey: '',
    baseLanguage: 'en',
    targetLanguages: [],
    translationPrompt: DEFAULT_TRANSLATION_PROMPT,
    selectedResourceTypes: ['PRODUCT', 'COLLECTION', 'ONLINE_STORE_PAGE'],
    maxTokens: 4096,
    temperature: 0.5,
    autoTranslate: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [saveMessage, setSaveMessage] = useState('');
  const [shopLocales, setShopLocales] = useState<ShopLocale[]>([]);
  const [isLoadingLocales, setIsLoadingLocales] = useState(false);
  const [showPromptTemplates, setShowPromptTemplates] = useState(false);
  const [resourceCategoryFilter, setResourceCategoryFilter] = useState('all');
  const [resourceSearchTerm, setResourceSearchTerm] = useState('');
  const [showAdvancedResourceFiltering, setShowAdvancedResourceFiltering] = useState(false);
  const [subscription, setSubscription] = useState<any>(null);
  const [planLimits, setPlanLimits] = useState<any>(null);
  const [currentUsage, setCurrentUsage] = useState({ languages: 0, translations: 0 });
  const [showTestPromptModal, setShowTestPromptModal] = useState(false);

  useEffect(() => {
    loadSettings();
    loadShopLocales();
    loadSubscription();
  }, []);

  // Clean up target languages when shop locales change
  useEffect(() => {
    if (shopLocales.length > 0) {
      cleanupTargetLanguages();
    }
  }, [shopLocales]);

  const loadSubscription = async () => {
    try {
      const subscriptionData = await billingService.getSubscriptionStatus();
      setSubscription(subscriptionData);
      
      // Get plan limits
      const plan = getPlanById(subscriptionData.plan || 'starter');
      if (plan) {
        setPlanLimits(plan.limits);
      }
    } catch (error) {
      console.error('Failed to load subscription:', error);
    }
  };

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.getSettings();
      if (response.data) {
        setSettings(response.data);
        
        // Load real usage data
        await loadUsageData();
      } else {
        console.error('Failed to load settings:', response.error);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsageData = async () => {
    try {
      const usageResponse = await apiService.getUserUsage();
      if (usageResponse.data) {
        setCurrentUsage({
          languages: usageResponse.data.languages,
          translations: usageResponse.data.translations
        });
      }
    } catch (error) {
      console.error('Failed to load usage data:', error);
    }
  };

  const loadShopLocales = async () => {
    setIsLoadingLocales(true);
    try {
      // First get the user's stores
      const storesResponse = await apiService.getStores();
      
      // Handle API Platform Collection format
      let stores: any[] = [];
      if (storesResponse.data) {
        const responseData = storesResponse.data as any;
        if (Array.isArray(responseData)) {
          // Direct array response
          stores = responseData;
        } else if (responseData['hydra:member']) {
          // API Platform Collection format
          stores = responseData['hydra:member'];
        } else if (responseData.member) {
          // Alternative Collection format
          stores = responseData.member;
        }
      }

      if (stores.length > 0) {
        const storeId = stores[0].id; // Assuming the first store is the relevant one
        const localesResponse = await apiService.getStoreLocales(storeId);
        
        if (localesResponse.data && Array.isArray(localesResponse.data)) {
          setShopLocales(localesResponse.data);
          
          // Set base language to the primary language from the store
          const primaryLanguage = localesResponse.data.find((locale: any) => locale.primary);
          if (primaryLanguage) {
            setSettings(prev => ({
              ...prev,
              baseLanguage: primaryLanguage.locale
            }));
          }
        } else if (localesResponse.data && localesResponse.data['hydra:member']) {
          setShopLocales(localesResponse.data['hydra:member']);
          
          // Set base language to the primary language from the store
          const primaryLanguage = localesResponse.data['hydra:member'].find((locale: any) => locale.primary);
          if (primaryLanguage) {
            setSettings(prev => ({
              ...prev,
              baseLanguage: primaryLanguage.locale
            }));
          }
        } else if (localesResponse.data && typeof localesResponse.data === 'object') {
          // Handle case where data is a direct object with all_locales and published_locales
          
          if (localesResponse.data.all_locales && Array.isArray(localesResponse.data.all_locales)) {
            // Transform the data to match our expected format
            const transformedLocales = localesResponse.data.all_locales.map((locale: any) => ({
              locale: locale.locale,
              name: getLanguageLabel(locale.locale),
              primary: locale.primary || false,
              published: locale.published || false,
              enabled: locale.published || false
            }));
            
            setShopLocales(transformedLocales);
            
            // Set base language to the primary language from the store
            const primaryLanguage = transformedLocales.find((locale: any) => locale.primary);
            if (primaryLanguage) {
              setSettings(prev => ({
                ...prev,
                baseLanguage: primaryLanguage.locale
              }));
            }
          } else {
            // Fallback to single locale object
            const locale = localesResponse.data;
            const transformedLocale = {
              locale: locale.locale,
              name: getLanguageLabel(locale.locale),
              primary: locale.primary || false,
              published: locale.published || false,
              enabled: locale.published || false
            };
            setShopLocales([transformedLocale]);
            
            if (transformedLocale.primary) {
              setSettings(prev => ({
                ...prev,
                baseLanguage: transformedLocale.locale
              }));
            }
          }
        } else {
          // No languages found - set empty array
          setShopLocales([]);
        }
      }
      
      // Clean up target languages after loading shop locales
      setTimeout(() => cleanupTargetLanguages(), 100);
    } catch (error) {
      console.error('Failed to load shop locales:', error);
      setShopLocales([]);
    } finally {
      setIsLoadingLocales(false);
    }
  };

  const saveSettings = async () => {
    setIsLoading(true);
    setSaveStatus('idle');
    
    try {
      const response = await apiService.saveSettings(settings);
      if (response.data) {
        setSaveStatus('success');
        setSaveMessage('Settings saved successfully!');
      } else {
        setSaveStatus('error');
        setSaveMessage(response.error || 'Failed to save settings');
      }
    } catch (error) {
      setSaveStatus('error');
      setSaveMessage('Failed to save settings');
    } finally {
      setIsLoading(false);
    }
  };

  const getAvailableLanguagesFromShop = () => {
    if (!shopLocales || shopLocales.length === 0) {
      return AVAILABLE_LANGUAGES; // Fallback to hardcoded list
    }
    
    return shopLocales.map(locale => ({
      label: getLanguageLabel(locale.locale),
      value: locale.locale
    }));
  };

  // Function to automatically remove target languages that are no longer available in the shop
  const cleanupTargetLanguages = () => {
    if (!shopLocales || shopLocales.length === 0) return;
    
    const availableLocales = shopLocales.map(locale => locale.locale);
    const removedLanguages: string[] = [];
    
    setSettings(prev => {
      const newTargetLanguages = prev.targetLanguages.filter(lang => {
        if (!availableLocales.includes(lang)) {
          removedLanguages.push(lang);
          return false; // Remove this language
        }
        return true; // Keep this language
      });
      
      if (removedLanguages.length > 0) {
        console.log(`🔄 Automatically removed unavailable languages from target languages: ${removedLanguages.join(', ')}`);
      }
      
      return {
        ...prev,
        targetLanguages: newTargetLanguages
      };
    });
  };

  const getLanguageLabel = (value: string) => {
    const language = AVAILABLE_LANGUAGES.find(lang => lang.value === value);
    return language ? language.label : value;
  };

  const getResourceTypeLabel = (value: string) => {
    const resource = RESOURCE_TYPES.find(res => res.value === value);
    return resource ? resource.label : value;
  };

  const applyPromptTemplate = (template: PromptTemplate) => {
    // Replace placeholder with actual target language
    const targetLanguage = settings.targetLanguages.length > 0 
      ? getLanguageLabel(settings.targetLanguages[0]) 
      : 'the target language';
    
    const prompt = template.prompt.replace('{target_language}', targetLanguage);
    setSettings(prev => ({ ...prev, translationPrompt: prompt }));
    setShowPromptTemplates(false);
  };

  const getFilteredResources = () => {
    return RESOURCE_TYPES.filter(resource => {
      const matchesCategory = resourceCategoryFilter === 'all' || resource.category === resourceCategoryFilter;
      const matchesSearch = resource.label.toLowerCase().includes(resourceSearchTerm.toLowerCase()) ||
                           resource.description.toLowerCase().includes(resourceSearchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  };

  const selectAllResources = () => {
    const filteredResources = getFilteredResources();
    const filteredValues = filteredResources.map(r => r.value);
    setSettings(prev => ({
      ...prev,
      selectedResourceTypes: [...new Set([...prev.selectedResourceTypes, ...filteredValues])]
    }));
  };

  const deselectAllResources = () => {
    const filteredResources = getFilteredResources();
    const filteredValues = filteredResources.map(r => r.value);
    setSettings(prev => ({
      ...prev,
      selectedResourceTypes: prev.selectedResourceTypes.filter(r => !filteredValues.includes(r))
    }));
  };

  const getCategoryCount = (category: string) => {
    if (category === 'all') return RESOURCE_TYPES.length;
    return RESOURCE_TYPES.filter(r => r.category === category).length;
  };

  const toggleLanguageStatus = async (locale: string, published: boolean) => {
    try {
      const storesResponse = await apiService.getStores();
      let stores: any[] = [];
      if (storesResponse.data) {
        const responseData = storesResponse.data as any;
        if (Array.isArray(responseData)) {
          stores = responseData;
        } else if (responseData['hydra:member']) {
          stores = responseData['hydra:member'];
        } else if (responseData.member) {
          stores = responseData.member;
        }
      }

      if (stores.length > 0) {
        const storeId = stores[0].id;
        
        const response = await fetch(`https://app.translationflow.microdesk.space/api/translations/stores/${storeId}/locales`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({
            action: published ? 'publish' : 'unpublish',
            locale: locale
          })
        });

        if (response.ok) {
          // Reload the locales to get updated data
          await loadShopLocales();
        } else {
          console.error(`❌ Failed to ${published ? 'publish' : 'unpublish'} language ${locale}`);
        }
      }
    } catch (error) {
      console.error('Failed to toggle language status:', error);
    }
  };

  const removeLanguage = async (locale: string) => {
    try {
      const storesResponse = await apiService.getStores();
      let stores: any[] = [];
      if (storesResponse.data) {
        const responseData = storesResponse.data as any;
        if (Array.isArray(responseData)) {
          stores = responseData;
        } else if (responseData['hydra:member']) {
          stores = responseData['hydra:member'];
        } else if (responseData.member) {
          stores = responseData.member;
        }

      }

      if (stores.length > 0) {
        const storeId = stores[0].id;
        
        const response = await fetch(`https://app.translationflow.microdesk.space/api/translations/stores/${storeId}/locales`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({
            action: 'remove',
            locale: locale
          })
        });

        if (response.ok) {
          // Reload the locales to get updated data
          await loadShopLocales();
          // Also clean up target languages immediately
          setTimeout(() => cleanupTargetLanguages(), 100);
        } else {
          console.error(`❌ Failed to remove language ${locale}`);
        }
      }
    } catch (error) {
      console.error('Failed to remove language:', error);
    }
  };

  return (
    <Page title="Settings">
      <Layout>
        <Layout.Section>
          <Banner title="NEW SETTINGS PAGE" tone="success">
            This is the new enhanced Settings page with all configuration options!
          </Banner>
        </Layout.Section>

        <Layout.Section>
          {saveStatus === 'success' && (
            <Banner title="Success" tone="success" onDismiss={() => setSaveStatus('idle')}>
              {saveMessage}
            </Banner>
          )}
          
          {saveStatus === 'error' && (
            <Banner title="Error" tone="critical" onDismiss={() => setSaveStatus('idle')}>
              {saveMessage}
            </Banner>
          )}
        </Layout.Section>

        {/* Plan Information Section */}
        {subscription && (
          <Layout.Section>
            <Card>
              <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <TextContainer>
                    <Text variant="headingMd" as="h2">
                      Plan Information
                    </Text>
                    <Text variant="bodyMd" as="p">
                      Your current subscription and usage limits.
                    </Text>
                  </TextContainer>
                  <Button variant="primary" onClick={() => window.location.href = '/billing'}>
                    Manage Plan
                  </Button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ padding: '16px', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                    <Text variant="bodyMd" as="p" fontWeight="semibold">
                      Current Plan
                    </Text>
                    <Text variant="headingLg" as="h3" tone="success">
                      {subscription.plan ? subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1) : 'Starter'} Plan
                    </Text>
                    <Text variant="bodySm" as="p" tone="subdued">
                      {subscription.status === 'active' ? 'Active' : subscription.status}
                    </Text>
                  </div>

                  <div style={{ padding: '16px', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                    <Text variant="bodyMd" as="p" fontWeight="semibold">
                      Languages Used
                    </Text>
                    <Text variant="headingLg" as="h3">
                      {currentUsage.languages}
                      {planLimits && (
                        <span style={{ fontSize: '14px', color: '#637381' }}>
                          /{planLimits.languages === -1 ? '∞' : planLimits.languages}
                        </span>
                      )}
                    </Text>
                    <Text variant="bodySm" as="p" tone="subdued">
                      {planLimits && planLimits.languages !== -1 ? 
                        `${Math.max(0, planLimits.languages - currentUsage.languages)} remaining` : 
                        'Unlimited'
                      }
                    </Text>
                  </div>

                  <div style={{ padding: '16px', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                    <Text variant="bodyMd" as="p" fontWeight="semibold">
                      Translations This Month
                    </Text>
                    <Text variant="headingLg" as="h3">
                      {currentUsage.translations}
                      {planLimits && (
                        <span style={{ fontSize: '14px', color: '#637381' }}>
                          /{planLimits.translations_per_month === -1 ? '∞' : planLimits.translations_per_month.toLocaleString()}
                        </span>
                      )}
                    </Text>
                    <Text variant="bodySm" as="p" tone="subdued">
                      {planLimits && planLimits.translations_per_month !== -1 ? 
                        `${Math.max(0, planLimits.translations_per_month - currentUsage.translations).toLocaleString()} remaining` : 
                        'Unlimited'
                      }
                    </Text>
                  </div>
                </div>

                {planLimits && (
                  <div style={{ padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                    <Text variant="bodyMd" as="p" fontWeight="semibold" tone="success">
                      Plan Features
                    </Text>
                    <div style={{ marginTop: '8px' }}>
                      {getPlanById(subscription.plan || 'starter')?.features.map((feature, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ color: '#059669', marginRight: '8px' }}>✓</span>
                          <Text variant="bodySm" as="span">{feature}</Text>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <div style={{ padding: '20px' }}>
              <TextContainer>
                <Text variant="headingMd" as="h2">
                  API Configuration
                </Text>
                <Text variant="bodyMd" as="p">
                  Configure your OpenAI API key for AI-powered translations.
                </Text>
              </TextContainer>

              <FormLayout>
                <TextField
                  label="OpenAI API Key"
                  value={settings.openaiApiKey}
                  onChange={(value) => setSettings(prev => ({ ...prev, openaiApiKey: value }))}
                  type="password"
                  helpText="Your OpenAI API key for GPT-3.5-turbo translations"
                  placeholder="sk-..."
                  autoComplete="off"
                />
              </FormLayout>
            </div>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <div style={{ padding: '20px' }}>
              <TextContainer>
                <Text variant="headingMd" as="h2">
                  Language Configuration
                </Text>
                <Text variant="bodyMd" as="p">
                  Select your base language and target languages for translation.
                </Text>
              </TextContainer>

              <FormLayout>
                <Select
                  label="Base Language"
                  options={AVAILABLE_LANGUAGES}
                  value={settings.baseLanguage}
                  onChange={(value) => setSettings(prev => ({ ...prev, baseLanguage: value }))}
                  helpText="The source language for translations"
                />

                <div>
                  <Text variant="bodyMd" as="p" fontWeight="semibold">
                    Target Languages (select multiple):
                    {planLimits && (
                      <span style={{ color: '#637381', fontWeight: 'normal' }}>
                        {' '}({settings.targetLanguages.length}/{planLimits.languages === -1 ? '∞' : planLimits.languages})
                      </span>
                    )}
                    <span style={{ color: '#637381', fontWeight: 'normal', fontSize: '14px' }}>
                      {' '}({getAvailableLanguagesFromShop().length} available in shop)
                    </span>
                  </Text>
                  {planLimits && settings.targetLanguages.length >= planLimits.languages && planLimits.languages !== -1 && (
                    <div style={{ marginBottom: '12px' }}>
                      <Banner tone="warning">
                        <p>You've reached the maximum number of languages for your {subscription?.plan || 'starter'} plan. 
                        <Button variant="plain" onClick={() => window.location.href = '/billing'}>
                          Upgrade your plan
                        </Button> to add more languages.</p>
                      </Banner>
                    </div>
                  )}
                  <div style={{ marginTop: '8px' }}>
                    {getAvailableLanguagesFromShop().map(lang => {
                      const isDisabled = planLimits && 
                        planLimits.languages !== -1 && 
                        !settings.targetLanguages.includes(lang.value) && 
                        settings.targetLanguages.length >= planLimits.languages;
                      
                      return (
                        <label key={lang.value} style={{ 
                          display: 'block', 
                          marginBottom: '8px',
                          opacity: isDisabled ? 0.5 : 1,
                          cursor: isDisabled ? 'not-allowed' : 'pointer'
                        }}>
                          <input
                            type="checkbox"
                            checked={settings.targetLanguages.includes(lang.value)}
                            disabled={isDisabled}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSettings(prev => ({
                                  ...prev,
                                  targetLanguages: [...prev.targetLanguages, lang.value]
                                }));
                              } else {
                                setSettings(prev => ({
                                  ...prev,
                                  targetLanguages: prev.targetLanguages.filter(l => l !== lang.value)
                                }));
                              }
                            }}
                            style={{ marginRight: '8px' }}
                          />
                          {lang.label}
                          {isDisabled && (
                            <span style={{ color: '#637381', fontSize: '12px', marginLeft: '8px' }}>
                              (Plan limit reached)
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {settings.targetLanguages.length > 0 && (
                  <div>
                    <Text variant="bodyMd" as="p" fontWeight="semibold">
                      Selected Languages:
                    </Text>
                    <div style={{ marginTop: '8px' }}>
                      {settings.targetLanguages.map(lang => (
                        <Badge key={lang} tone="info">
                          {getLanguageLabel(lang)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </FormLayout>
            </div>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <TextContainer>
                  <Text variant="headingMd" as="h2">
                    Resource Targeting
                  </Text>
                  <Text variant="bodyMd" as="p">
                    Select which Shopify resources should be translated.
                  </Text>
                </TextContainer>
                
                <Button
                  variant="secondary"
                  onClick={() => setShowAdvancedResourceFiltering(!showAdvancedResourceFiltering)}
                >
                  {showAdvancedResourceFiltering ? 'Hide Filters' : 'Advanced Filtering'}
                </Button>
              </div>

              {showAdvancedResourceFiltering && (
                <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <Text variant="bodyMd" as="p" fontWeight="semibold">
                      Advanced Filtering
                    </Text>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <div style={{ marginBottom: '4px' }}>
                        <Text variant="bodySm" as="p" fontWeight="semibold">
                          Category Filter
                        </Text>
                      </div>
                      <Select
                        label=""
                        options={RESOURCE_CATEGORIES.map(cat => ({
                          label: `${cat.label} (${getCategoryCount(cat.value)})`,
                          value: cat.value
                        }))}
                        value={resourceCategoryFilter}
                        onChange={(value) => setResourceCategoryFilter(value)}
                      />
                    </div>
                    
                    <div>
                      <div style={{ marginBottom: '4px' }}>
                        <Text variant="bodySm" as="p" fontWeight="semibold">
                          Search Resources
                        </Text>
                      </div>
                      <TextField
                        label=""
                        value={resourceSearchTerm}
                        onChange={(value) => setResourceSearchTerm(value)}
                        placeholder="Search resource types..."
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      variant="primary"
                      size="slim"
                      onClick={selectAllResources}
                    >
                      Select All Filtered
                    </Button>
                    <Button
                      variant="secondary"
                      size="slim"
                      onClick={deselectAllResources}
                    >
                      Deselect All Filtered
                    </Button>
                  </div>
                </div>
              )}

              <FormLayout>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Text variant="bodyMd" as="p" fontWeight="semibold">
                      Resource Types ({getFilteredResources().length} available):
                    </Text>
                    <Text variant="bodySm" as="p" tone="subdued">
                      {settings.selectedResourceTypes.length} selected
                    </Text>
                  </div>
                  
                  <div style={{ 
                    maxHeight: '300px', 
                    overflowY: 'auto', 
                    border: '1px solid #e1e3e5', 
                    borderRadius: '8px', 
                    padding: '12px' 
                  }}>
                    {getFilteredResources().map(resource => (
                      <div key={resource.value} style={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        padding: '8px', 
                        borderBottom: '1px solid #f1f2f3',
                        backgroundColor: settings.selectedResourceTypes.includes(resource.value) ? '#f6f6f7' : 'transparent'
                      }}>
                        <input
                          type="checkbox"
                          checked={settings.selectedResourceTypes.includes(resource.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSettings(prev => ({
                                ...prev,
                                selectedResourceTypes: [...prev.selectedResourceTypes, resource.value]
                              }));
                            } else {
                              setSettings(prev => ({
                                ...prev,
                                selectedResourceTypes: prev.selectedResourceTypes.filter(r => r !== resource.value)
                              }));
                            }
                          }}
                          style={{ marginRight: '12px', marginTop: '2px' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <Text variant="bodyMd" as="p" fontWeight="semibold">
                              {resource.label}
                            </Text>
                            <Badge tone="info">{resource.category}</Badge>
                          </div>
                          <Text variant="bodySm" as="p" tone="subdued">
                            {resource.description}
                          </Text>
                        </div>
                      </div>
                    ))}
                    
                    {getFilteredResources().length === 0 && (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Text variant="bodyMd" as="p">No resources match your current filters.</Text>
                      </div>
                    )}
                  </div>
                </div>

                {settings.selectedResourceTypes.length > 0 && (
                  <div>
                    <Text variant="bodyMd" as="p" fontWeight="semibold">
                      Selected Resources ({settings.selectedResourceTypes.length}):
                    </Text>
                    <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {settings.selectedResourceTypes.map(resource => (
                        <Badge key={resource} tone="info">
                          {getResourceTypeLabel(resource)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </FormLayout>
            </div>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <div style={{ padding: '20px' }}>
              <TextContainer>
                <Text variant="headingMd" as="h2">
                  Translation Settings
                </Text>
                <Text variant="bodyMd" as="p">
                  Configure AI translation parameters and behavior.
                </Text>
              </TextContainer>

              <FormLayout>
                <TextField
                  label="Max Tokens"
                  type="number"
                  value={settings.maxTokens.toString()}
                  onChange={(value) => setSettings(prev => ({ ...prev, maxTokens: parseInt(value) || 4096 }))}
                  helpText="Maximum tokens for AI responses (default: 4096)"
                  autoComplete="off"
                />

                <TextField
                  label="Temperature"
                  type="number"
                  value={settings.temperature.toString()}
                  onChange={(value) => setSettings(prev => ({ ...prev, temperature: parseFloat(value) || 0.5 }))}
                  helpText="AI creativity level (0.0 = focused, 1.0 = creative)"
                  step={0.1}
                  min="0"
                  max="1"
                  autoComplete="off"
                />

                <Checkbox
                  label="Auto-translate new content"
                  checked={settings.autoTranslate}
                  onChange={(checked) => setSettings(prev => ({ ...prev, autoTranslate: checked }))}
                  helpText="Automatically translate new content when it's created"
                />
              </FormLayout>
            </div>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <TextContainer>
                  <Text variant="headingMd" as="h2">
                    Translation Prompt
                  </Text>
                  <Text variant="bodyMd" as="p">
                    Customize the AI prompt used for translations. Use placeholders: base_language, translate_to, text
                  </Text>
                </TextContainer>
                
                <Button
                  variant="secondary"
                  onClick={() => setShowPromptTemplates(!showPromptTemplates)}
                >
                  {showPromptTemplates ? 'Hide Templates' : 'Show Templates'}
                </Button>
              </div>

              {showPromptTemplates && (
                <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <Text variant="bodyMd" as="p" fontWeight="semibold">
                      Prompt Templates
                    </Text>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <Text variant="bodySm" as="p">
                      Choose a pre-built template or create your own custom prompt.
                    </Text>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '12px' }}>
                    {PROMPT_TEMPLATES.map(template => (
                      <Card key={template.id}>
                        <div style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                            <div>
                              <Text variant="bodyMd" as="p" fontWeight="semibold">
                                {template.name}
                              </Text>
                              <Text variant="bodySm" as="p" tone="subdued">
                                {template.description}
                              </Text>
                            </div>
                            <Badge tone="info">{template.category}</Badge>
                          </div>
                          <Button
                            variant="primary"
                            size="slim"
                            onClick={() => applyPromptTemplate(template)}
                            fullWidth
                          >
                            Use Template
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Custom Prompt"
                    value={settings.translationPrompt}
                    onChange={(value) => setSettings(prev => ({ ...prev, translationPrompt: value }))}
                    multiline={10}
                    helpText="Custom instructions for the AI translation model"
                    autoComplete="off"
                  />
                </div>
                <div style={{ marginTop: '24px' }}>
                  <Button
                    onClick={() => setShowTestPromptModal(true)}
                    variant="secondary"
                  >
                    Test Prompt
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <div style={{ padding: '16px' }}>
              <Text variant="headingMd" as="h2">
                Shop Language Management
              </Text>
              <Text variant="bodyMd" as="p" tone="subdued">
                Manage the languages installed in your Shopify store. Enable or disable languages and add new ones.
              </Text>
              
              {isLoadingLocales ? (
                <div style={{ padding: '16px 0' }}>
                  <Text variant="bodyMd" as="p">Loading shop languages...</Text>
                </div>
              ) : shopLocales.length > 0 ? (
                <div style={{ marginTop: '16px' }}>
                  <Text variant="headingSm" as="h3">Installed Languages ({shopLocales.length})</Text>
                  <div style={{ marginTop: '12px' }}>
                    {shopLocales.map((locale: any) => (
                      <div
                        key={locale.locale}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px',
                          border: '1px solid #e1e3e5',
                          borderRadius: '8px',
                          marginBottom: '8px',
                          backgroundColor: locale.published ? '#f6f6f7' : '#ffffff'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Checkbox
                            label=""
                            checked={locale.published}
                            onChange={() => {
                              // Toggle published status
                              const updatedLocales = shopLocales.map((l: any) =>
                                l.locale === locale.locale
                                  ? { ...l, published: !l.published }
                                  : l
                              );
                              setShopLocales(updatedLocales);
                              // Call backend to update
                              toggleLanguageStatus(locale.locale, !locale.published);
                            }}
                          />
                          <div>
                            <Text variant="bodyMd" as="p">
                              {locale.name} ({locale.locale})
                            </Text>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                              {locale.primary && (
                                <span style={{
                                  backgroundColor: '#008060',
                                  color: 'white',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '12px'
                                }}>
                                  Default
                                </span>
                              )}
                              {locale.published && (
                                <span style={{
                                  backgroundColor: '#5c6ac4',
                                  color: 'white',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '12px'
                                }}>
                                  Published
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button
                            size="slim"
                            variant={locale.published ? "plain" : "primary"}
                            onClick={() => {
                              // Toggle published status
                              const updatedLocales = shopLocales.map((l: any) =>
                                l.locale === locale.locale
                                  ? { ...l, published: !l.published }
                                  : l
                              );
                              setShopLocales(updatedLocales);
                              // Call backend to update
                              toggleLanguageStatus(locale.locale, !locale.published);
                            }}
                          >
                            {locale.published ? 'Unpublish' : 'Publish'}
                          </Button>
                          {!locale.primary && (
                            <Button
                              size="slim"
                              variant="plain"
                              tone="critical"
                              onClick={() => {
                                // Remove language (except primary)
                                const updatedLocales = shopLocales.filter((l: any) => l.locale !== locale.locale);
                                setShopLocales(updatedLocales);
                                // Call backend to remove
                                removeLanguage(locale.locale);
                              }}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add New Language Section */}
                  <div style={{ marginTop: '24px' }}>
                    <Text variant="headingSm" as="h3">Add New Language</Text>
                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #c9cccf',
                          borderRadius: '4px',
                          fontSize: '14px',
                          minWidth: '200px'
                        }}
                        onChange={async (e) => {
                          if (e.target.value) {
                            const selectedLocale = e.target.value;
                            
                            try {
                              const storesResponse = await apiService.getStores();
                              let stores: any[] = [];
                              if (storesResponse.data) {
                                const responseData = storesResponse.data as any;
                                if (Array.isArray(responseData)) {
                                  stores = responseData;
                                } else if (responseData['hydra:member']) {
                                  stores = responseData['hydra:member'];
                                } else if (responseData.member) {
                                  stores = responseData.member;
                                }
                              }

                              if (stores.length > 0) {
                                const storeId = stores[0].id;
                                
                                // Call backend to add the language
                                const response = await fetch(`https://app.translationflow.microdesk.space/api/translations/stores/${storeId}/locales`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                                  },
                                  body: JSON.stringify({
                                    action: 'add',
                                    locale: selectedLocale
                                  })
                                });

                                const result = await response.json();
                                
                                if (response.ok && result.success) {
                                  // Reload the locales to get updated data
                                  await loadShopLocales();
                                } else {
                                  console.error('❌ Failed to add language:', result);
                                  alert(`Failed to add language: ${result.result?.error || 'Unknown error'}`);
                                }
                              }
                            } catch (error) {
                              console.error('Error adding language:', error);
                              alert('Failed to add language. Please try again.');
                            }
                            
                            e.target.value = '';
                          }
                        }}
                      >
                        <option value="">Select a language to add...</option>
                        <option value="en">English</option>
                        <option value="es">Spanish</option>
                        <option value="de">German</option>
                        <option value="it">Italian</option>
                        <option value="pt">Portuguese</option>
                        <option value="nl">Dutch</option>
                        <option value="ja">Japanese</option>
                        <option value="ko">Korean</option>
                        <option value="zh">Chinese</option>
                        <option value="ar">Arabic</option>
                      </select>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '16px 0' }}>
                  <Text variant="bodyMd" as="p" tone="subdued">
                    No languages found. Please connect your Shopify store first.
                  </Text>
                </div>
              )}
            </div>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <Button onClick={loadSettings} disabled={isLoading}>
              Reset
            </Button>
            <Button variant="primary" onClick={saveSettings} loading={isLoading}>
              Save Settings
            </Button>
          </div>
        </Layout.Section>
      </Layout>

      <TestPromptModal
        open={showTestPromptModal}
        onClose={() => setShowTestPromptModal(false)}
        currentPrompt={settings.translationPrompt}
        targetLanguages={settings.targetLanguages}
        baseLanguage={settings.baseLanguage}
      />
    </Page>
  );
}; 
